import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FileText, Plus, LogOut, Settings as SettingsIcon } from "lucide-react";
import { Toaster } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium btn-lift ${
        isActive
          ? "bg-[#2C5545] text-white"
          : "text-[#5C665E] hover:bg-[#F9F8F6]"
      }`
    }
  >
    <Icon size={18} strokeWidth={1.75} />
    <span>{label}</span>
  </NavLink>
);

export default function Layout() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };
  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "#F9F8F6" }}>
      {/* Sidebar */}
      <aside
        className="w-64 border-r hidden md:flex flex-col"
        style={{ backgroundColor: "#FFFFFF", borderColor: "#E5E2DC" }}
      >
        <div className="p-6 border-b" style={{ borderColor: "#E5E2DC" }}>
          <div className="flex items-center gap-2.5">
            <img src="/logo-yacoclean.jpg" alt="YACOCLEAN" data-testid="sidebar-logo" className="w-11 h-11 rounded-xl object-cover" />
            <div>
              <div className="font-heading font-bold text-lg leading-none">
                YACOCLEAN
              </div>
              <div className="text-xs text-[#5C665E] mt-0.5">
                Suivi de factures
              </div>
            </div>
          </div>
        </div>

        <nav className="p-4 flex flex-col gap-1 flex-1">
          <NavItem
            to="/"
            icon={LayoutDashboard}
            label="Tableau de bord"
            testId="nav-dashboard"
          />
          <NavItem
            to="/factures"
            icon={FileText}
            label="Factures"
            testId="nav-factures"
          />
          <NavItem
            to="/clients"
            icon={Users}
            label="Clients"
            testId="nav-clients"
          />
          <NavItem
            to="/settings"
            icon={SettingsIcon}
            label="Réglages"
            testId="nav-settings"
          />
        </nav>

        <div className="p-4 border-t space-y-3" style={{ borderColor: "#E5E2DC" }}>
          <button
            data-testid="new-invoice-cta"
            onClick={() => navigate("/factures/nouveau")}
            className="w-full flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold text-white btn-lift focus-brand"
            style={{ backgroundColor: "#2C5545" }}
          >
            <Plus size={16} />
            Nouvelle facture
          </button>

          {user && typeof user === "object" && (
            <div className="flex items-center justify-between gap-2 px-1">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-[#5C665E]">Connecté</div>
                <div className="text-xs font-medium truncate" data-testid="current-user-email">
                  {user.email}
                </div>
              </div>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                title="Se déconnecter"
                className="p-2 rounded-lg text-[#5C665E] hover:text-[#B3261E] hover:bg-[#FCE8E8] btn-lift"
              >
                <LogOut size={14} />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b flex items-center justify-between px-4 py-3" style={{ borderColor: "#E5E2DC" }}>
        <div className="flex items-center gap-2">
          <img src="/logo-yacoclean.jpg" alt="YACOCLEAN" data-testid="mobile-logo" className="w-8 h-8 rounded-lg object-cover" />
          <span className="font-heading font-bold">YACOCLEAN</span>
        </div>
        <div className="flex gap-1 text-xs">
          <NavLink to="/" end data-testid="nav-mobile-dashboard" className={({isActive})=>`px-2.5 py-1.5 rounded-md ${isActive?'bg-[#2C5545] text-white':'text-[#5C665E]'}`}>Tableau</NavLink>
          <NavLink to="/factures" data-testid="nav-mobile-factures" className={({isActive})=>`px-2.5 py-1.5 rounded-md ${isActive?'bg-[#2C5545] text-white':'text-[#5C665E]'}`}>Factures</NavLink>
          <NavLink to="/clients" data-testid="nav-mobile-clients" className={({isActive})=>`px-2.5 py-1.5 rounded-md ${isActive?'bg-[#2C5545] text-white':'text-[#5C665E]'}`}>Clients</NavLink>
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 overflow-auto mt-14 md:mt-0">
        <div className="max-w-6xl px-6 md:px-10 py-8 md:py-12">
          <Outlet />
        </div>
      </main>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}
